import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { signUpRequest } from '../../store/account/userActions';
import { NavLink } from 'react-router-dom';
import './SignUp.scss';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  UserPlus, 
  CheckCircle, 
  AlertCircle,
  Sparkles,
  LogIn
} from 'lucide-react';

function SignUp({ ...props }) {
  const { sign, history } = props;
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState({
    data: {
      username: '',
      password: '',
      email: '',
    },
    errors: {},
    passwordVisibility: false,
  });

  const handleTogglePasswordVisibility = () => {
    setState(prevState => ({
      ...prevState,
      passwordVisibility: !prevState.passwordVisibility,
    }));
  };

  const validatePassword = (password) => {
    const errors = [];

    if (!password || password.length < 8) {
      errors.push({ message: 'Must be at least 8 characters long.', isValid: false });
    } else {
      errors.push({ message: 'Must be at least 8 characters long.', isValid: true });
    }

    if (!/[A-Z]/.test(password)) {
      errors.push({ message: 'Must contain at least one uppercase letter.', isValid: false });
    } else {
      errors.push({ message: 'Must contain at least one uppercase letter.', isValid: true });
    }

    if (!/[a-z]/.test(password)) {
      errors.push({ message: 'Must contain at least one lowercase letter.', isValid: false });
    } else {
      errors.push({ message: 'Must contain at least one lowercase letter.', isValid: true });
    }

    if (!/\d/.test(password)) {
      errors.push({ message: 'Must contain at least one digit.', isValid: false });
    } else {
      errors.push({ message: 'Must contain at least one digit.', isValid: true });
    }

    const allowedSpecialCharRegex = /[@$!%*?&]/;
    const invalidSpecialCharRegex = /[^A-Za-z0-9@$!%*?&]/;
  
    if (!allowedSpecialCharRegex.test(password)) {
      errors.push({
        message: 'Must contain at least one special character (@, $, !, %, *, ?, &).',
        isValid: false,
      });
    } else if (invalidSpecialCharRegex.test(password)) {
      errors.push({
        message: 
        'Password contains invalid special characters. Only @, $, !, %, *, ?, & are allowed.',
        isValid: false,
      });
    } else {
      errors.push({
        message: 'Must contain at least one special character (@, $, !, %, *, ?, &).',
        isValid: true,
      });
    }
  
    return errors;
  };

  const validate = () => {
    const { data } = state;
    const errors = {};

    if (!data || typeof data !== 'object') {
      return errors;
    }

    if (!data.username) {
      errors.username = 'Username is required';
    } else if (data.username.length < 3) {
      errors.username = 'Username must be at least 3 characters long';
    }

    if (!data.email) {
      errors.email = 'Email address is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (!data.password) {
      errors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(data.password);
      const invalidPasswordErrors = passwordErrors.filter(error => !error.isValid);

      if (invalidPasswordErrors.length > 0) {
        errors.password = invalidPasswordErrors.map(error => error.message).join(' ');
      }
    }

    if (!captchaToken) {
      setCaptchaError('Please complete the captcha verification');
    } else {
      setCaptchaError('');
    }

    return errors;
  };

  const passwordErrors = validatePassword(state.data.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { data } = state;

    const errors = validate();

    if (Object.keys(errors).length > 0 || !captchaToken) {
      setState(prevState => ({
        ...prevState,
        errors,
      }));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        data,
        history: history,
      };

      await sign(payload);

      setState(prevState => ({
        ...prevState,
        errors: {},
        passwordVisibility: false,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [e.target.id]: e.target.value,
      },
      errors: {
        ...prevState.errors,
        [e.target.id]: '',
      },
    }));
  };

  const handleCaptchaVerification = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const { data, errors, passwordVisibility } = state;

  return (
    <div className="modern-signup-container">
      <div className="signup-wrapper">
        {/* Left Side - Welcome Section */}
        <div className="welcome-section">
          <div className="welcome-content">
            <div className="brand-section">
              <div className="brand-icon">
                <Sparkles size={40} />
              </div>
              <h1 className="brand-title">MultiSignals</h1>
            </div>
            
            <div className="welcome-text">
              <h2 className="welcome-title">
                Join the future of 
                <span className="highlight"> automated trading</span>
              </h2>
              <p className="welcome-subtitle">
                Create your account and start your trading journey today
              </p>
            </div>

            <div className="features-list">
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>Free Account Setup</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>Advanced Trading Tools</span>
              </div>
              <div className="feature-item">
                <CheckCircle size={20} className="feature-icon" />
                <span>24/7 Customer Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="signup-section">
          <div className="signup-form-container">
            <div className="form-header">
              <h2 className="form-title">Create Account</h2>
              <p className="form-subtitle">Start your trading journey with us</p>
            </div>

            <Form className="modern-signup-form" onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label className="form-label">
                  <User size={16} className="label-icon" />
                  Full Name
                </Form.Label>
                <div className="input-wrapper">
                  <Form.Control
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Enter your full name"
                    onChange={handleChange}
                    value={data?.username}
                    className={`modern-input ${errors?.username ? 'error' : ''}`}
                  />
                  <User size={18} className="input-icon" />
                </div>
                {errors?.username && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{errors.username}</span>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="form-label">
                  <Mail size={16} className="label-icon" />
                  Email Address
                </Form.Label>
                <div className="input-wrapper">
                  <Form.Control
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    onChange={handleChange}
                    value={data?.email}
                    className={`modern-input ${errors?.email ? 'error' : ''}`}
                  />
                  <Mail size={18} className="input-icon" />
                </div>
                {errors?.email && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{errors.email}</span>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="form-label">
                  <Lock size={16} className="label-icon" />
                  Password
                </Form.Label>
                <div className="input-wrapper">
                  <Form.Control
                    id="password"
                    type={passwordVisibility ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    onChange={handleChange}
                    value={data?.password}
                    className={`modern-input ${errors?.password ? 'error' : ''}`}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={handleTogglePasswordVisibility}
                  >
                    {passwordVisibility ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors?.password && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{errors.password}</span>
                  </div>
                )}
                
                {/* Password Requirements */}
                {data?.password && (
                  <div className="password-requirements">
                    {passwordErrors.map((error, index) => (
                      <div key={index} className={`requirement-item 
                      ${error.isValid ? 'valid' : 'invalid'}`}>
                        {error.isValid ? (
                          <CheckCircle size={12} className="requirement-icon" />
                        ) : (
                          <AlertCircle size={12} className="requirement-icon" />
                        )}
                        <span>{error.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>

              {/* Captcha */}
              <div className="captcha-section">
                <div className="captcha-wrapper">
                  <HCaptcha
                    sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                    onVerify={handleCaptchaVerification}
                  />
                </div>
                {captchaError && (
                  <div className="error-message">
                    <AlertCircle size={14} />
                    <span>{captchaError}</span>
                  </div>
                )}
              </div>

              <Button
                className="modern-signup-btn"
                variant="primary"
                type="submit"
                disabled={isLoading || !captchaToken}
              >
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <UserPlus size={18} />
                    <span className='m-2'>Create Account</span>
                  </>
                )}
              </Button>
            </Form>

            <div className="form-links">
              <div className="login-section">
                <span className="login-text">Already have an account?</span>
                <NavLink className="login-link" to="/login">
                  <LogIn size={16} />
                  Sign In
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    sign: (creds) => {
      dispatch(signUpRequest(creds));
    },
  };
};

export default connect(null, mapDispatchToProps)(SignUp);